import * as React from "react";
import Anchor from "atoms/anchors/anchor";
import Heading from "atoms/typography/heading";
import OnboardingFooter from "molecules/footer/onboarding-footer";
import Paragraph from "atoms/typography/paragraph";
import ProfileMenu from "organisms/profile/profile-menu";
import Stepper from "organisms/stepper/stepper";
import UserConfigurationErrorKeys from "constants/user-configuration-error-keys";
import logo from "assets/images/nfpa-link-color.png";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import {
    GroupContext,
    TeamOnboardingDetails,
    defaultState as TeamOnboardingDetailsDefaultState,
} from "utilities/contexts/group/group-context-provider";
import { NestedRoutes } from "utilities/routing/nested-route";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import { Redirect, useLocation } from "react-router";
import { RouteUtils } from "utilities/route-utils";
import { SkipNavContent, SkipNavLink } from "@reach/skip-nav";
import { TeamOnboardingSteps } from "utilities/contexts/stepper/step-context-provider";
import { siteMap } from "internal-sitemap";
import { useCallback, useEffect, useState } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useLocalization } from "utilities/hooks/use-localization";

const BASE_CLASS = "c-team-onboarding-layout";
const MENU_CLASS = "c-onboarding-layout__menu";

const TeamOnboardingLayout: React.FC<any> = (props: any) => {
    const { globalState } = useGlobalState();
    const { useTeamOnboarding } = useFeatureFlags();
    const isTeamOwner =
        globalState.currentIdentity?.getCurrentUserRole()?.userRoleGroup
            ?.isOwner;

    const { t } = useLocalization();
    const steps = [
        t("verifyTeamInformation"),
        t("manageTeamSettings"),
        t("individualizedExperience"),
    ];
    const [teamOnboardingDetails, setTeamOnboardingDetails] =
        useState<TeamOnboardingDetails>(TeamOnboardingDetailsDefaultState);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const location = useLocation();
    const pathName = location.pathname.split("/").pop();
    const [header, setHeader] = useState<string>(t("verifyTeamInformation"));

    const setGroupToDefault = useCallback(() => {
        setTeamOnboardingDetails({
            teamName: "",
            isNewTeam: false,
            groupId: null,
        });
    }, []);

    useEffect(() => {
        if (!teamOnboardingDetails) setGroupToDefault();
    }, [setGroupToDefault, teamOnboardingDetails]);

    useEffect(() => {
        if (pathName === "verify") {
            setCurrentStep(TeamOnboardingSteps.VerifyTeamInfo);
            setHeader(t("verifyTeamInformation"));
        } else if (pathName === "manage") {
            setCurrentStep(TeamOnboardingSteps.InviteTeamMembers);
            setHeader(t("manageTeamSettings"));
        } else if (pathName === "customize") {
            setCurrentStep(TeamOnboardingSteps.Customize);
            setHeader(t("individualizedExperience"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, pathName]);

    if (!isTeamOwner || !useTeamOnboarding) {
        return <Redirect to={siteMap.errors.notFound} />;
    }

    // If you do not need to onboard a team, redirect to the team management area in Account Information
    if (
        globalState.systemSettingsLoaded &&
        !globalState.unauthorizedResult?.hasErrorFor(
            UserConfigurationErrorKeys.ERROR_TEAM_NOT_INITIALIZED
        ) &&
        RouteUtils.assertIsNotCurrentUrl(siteMap.teamOnboarding.customize)
    ) {
        return <Redirect to={siteMap.account.team} />;
    }

    // If we lose the group's context (page refresh, etc.), bring user back to beginning
    // TODO: Warning on refresh/back to prevent them from losing work?
    if (
        !teamOnboardingDetails?.teamName &&
        RouteUtils.assertCurrentUrl(siteMap.teamOnboarding.manage)
    ) {
        return <Redirect to={siteMap.teamOnboarding.verify} />;
    }

    return (
        <>
            <GroupContext.Provider
                value={[teamOnboardingDetails, setTeamOnboardingDetails]}>
                <React.Fragment>
                    <SkipNavLink>{t("skipToMainContent")}</SkipNavLink>
                    <div className={BASE_CLASS}>
                        <div className={`${BASE_CLASS}__menu`}>
                            <div className={`${BASE_CLASS}__menu__logo`}>
                                <Anchor to={siteMap.home} ariaLabel="Home">
                                    <img src={logo} alt="Home" />
                                </Anchor>
                            </div>
                            <div className={`${MENU_CLASS}__auth`}>
                                <Paragraph
                                    cssClassName={`${MENU_CLASS}__auth__name`}
                                    size={ParagraphSizes.Small}>
                                    <span data-test-id={"signed-in-as"}>
                                        {`${t("signedInAs")} `}
                                    </span>
                                    <strong
                                        data-test-id={"signed-in-user-name"}>
                                        {globalState.currentIdentity?.user?.getFirstAndLastName()}
                                    </strong>
                                </Paragraph>
                                <ProfileMenu hideActions={true} />
                            </div>
                        </div>
                        <div className={`${BASE_CLASS}__content`}>
                            <SkipNavContent tabIndex={-1}>
                                <Heading
                                    cssClassName={`${BASE_CLASS}__content__header`}>
                                    {header}
                                </Heading>
                                <div
                                    className={`${BASE_CLASS}__content__stepper`}>
                                    <Stepper
                                        steps={steps}
                                        currentStep={currentStep}
                                    />
                                </div>

                                <NestedRoutes routes={props.routes} />

                                {props.children}
                            </SkipNavContent>
                        </div>
                        <OnboardingFooter />
                    </div>
                </React.Fragment>
            </GroupContext.Provider>
        </>
    );
};

export default TeamOnboardingLayout;
